import type { HostDataWriter, SyncVersion } from "@novorender/api";
import type { Bookmark } from "@novorender/data-js-api";
import pMap from "p-map";

import type { AuthScope, ModelTreePermission } from "apis/dataV2/authTypes";
import { bookmarkStatePath } from "apis/dataV2/dataV2Api";
import type { DeviationProjectConfig } from "apis/dataV2/deviationTypes";
import { grantedPermissions, Permission } from "apis/dataV2/permissions";
import type { ProjectInfo } from "apis/dataV2/projectTypes";
import { epsgDefinitionUrl } from "apis/epsg";
import type { BookmarkCatalogEntry } from "types/bookmark";
import { isVersionComparisonSceneId, parentSceneIdFromUrl } from "utils/sceneUrl";

import {
    type BookmarkScope,
    type CommittedDeviations,
    type CoordinateSystemSnapshot,
    HOST_DATA_SCHEMA_VERSION,
    type HostDataMeta,
    hostDataNames,
    type ModelTreePermissionsSnapshot,
    type PermissionsSnapshot,
    type ProjectSnapshot,
    type RawSceneConfig,
    type SceneIndex,
} from "./schema";

/** How many requests of one kind (bookmark states, group ids) a capture has in flight at once. */
const MAX_CONCURRENCY = 5;

/**
 * Where a capture asks for each thing. Every request goes to the network: a capture that can't
 * reach the server must fail, not capture what was captured before.
 */
export type HostDataSources = {
    sceneConfig(signal: AbortSignal): Promise<RawSceneConfig>;
    /** The scene index's `offline.manifest`: which version of the scene the server serves now. */
    sceneManifestId(signal: AbortSignal): Promise<string>;
    /** `null` for an empty answer, here and below: as the viewer's RTK Query reads one. */
    project(projectId: string, signal: AbortSignal): Promise<ProjectInfo | null>;
    /** `https://epsg.io/{epsg}.{format}`: its text, or `null` for an answer that isn't OK. */
    epsgDefinition(epsg: string, format: "wkt" | "proj4", signal: AbortSignal): Promise<string | null>;
    modelTreePermissions(projectId: string, signal: AbortSignal): Promise<ModelTreePermission[] | null>;
    permissions(scope: AuthScope, signal: AbortSignal): Promise<Permission[]>;
    bookmarkCatalog(scope: BookmarkScope, signal: AbortSignal): Promise<BookmarkCatalogEntry[]>;
    /** `undefined` when the bookmark is gone: deleted since the catalog listed it. */
    bookmarkState(scope: BookmarkScope, bookmarkId: string, signal: AbortSignal): Promise<Bookmark | null | undefined>;
    groupIds(groupId: string, signal: AbortSignal): Promise<number[] | null>;
    /** The saved deviation config; `null` for none saved (an empty answer); `undefined` for a 404. */
    deviationProfiles(signal: AbortSignal): Promise<DeviationProjectConfig | null | undefined>;
    /** The scene's `deviations.json` asset; `undefined` when it has none. */
    committedDeviations(signal: AbortSignal): Promise<DeviationProjectConfig | undefined>;
};

/**
 * Captures the open scene's host data into a full sync, as its `captureHostData` callback:
 * everything the offline-capable widgets and the offline scene open read (see `schema.ts`).
 * Bookmarks, group ids and deviations only with the permission that gates their widget.
 *
 * Rejects with the first request that fails, but for a bookmark deleted since the catalog listed
 * it, which is left out. Object ids are valid for the version the server serves now, so the
 * capture checks it serves `version` before asking for anything and again at the end, rejecting
 * with a {@link SceneChangedError} if a version was published meanwhile.
 */
export async function captureHostData({
    projectId,
    user,
    writer,
    signal: syncSignal,
    version,
    sources,
}: {
    /** The scene's id: a project scene's (offline is managed only there). */
    projectId: string;
    /** The signed-in user, whose personal bookmarks are captured; none without one. */
    user: string | undefined;
    writer: HostDataWriter;
    signal: AbortSignal;
    /** The version the sync downloaded: what the capture must be of. */
    version: SyncVersion;
    sources: HostDataSources;
}): Promise<void> {
    // One failure stops the rest: aborting what is in flight rather than waiting for it.
    const controller = new AbortController();
    const signal = AbortSignal.any([syncSignal, controller.signal]);
    const step = async <T>(run: () => Promise<T>): Promise<T> => {
        try {
            signal.throwIfAborted();
            return await run();
        } catch (error) {
            controller.abort(error);
            throw error;
        }
    };

    // Nothing is written once the capture is stopping, though a response already in hand.
    const write = async (name: string, value: unknown) => {
        signal.throwIfAborted();
        await writer.write(name, value);
    };

    const checkVersion = async () => {
        const served = await sources.sceneManifestId(signal);
        if (served !== version.manifestId) {
            throw new SceneChangedError(version.manifestId, served);
        }
    };

    await checkVersion();

    const sceneConfig = await sources.sceneConfig(signal);
    await write(hostDataNames.sceneConfig, sceneConfig);

    const sceneProjectId = sceneConfig.projectId ?? projectId;
    const isVersionComparison = isVersionComparisonSceneId(parentSceneIdFromUrl(new URL(sceneConfig.url)));
    const permissionScope: AuthScope = {
        organizationId: sceneConfig.organization,
        projectId: sceneProjectId,
        // Same scope as the viewer's own permission check: a project scene's has no viewer scene.
        viewerSceneId: projectId === sceneProjectId || isVersionComparison ? undefined : projectId,
    };
    const modelTreeProjectId = isVersionComparison ? sceneProjectId : projectId;

    const granted = await sources.permissions(permissionScope, signal);
    await write(hostDataNames.permissions, {
        args: { scope: permissionScope },
        data: granted,
    } satisfies PermissionsSnapshot);
    const readsBookmarks = granted.includes(Permission.BookmarkRead);
    const readsGroups = granted.includes(Permission.GroupRead);
    const readsDeviations = granted.includes(Permission.DeviationRead);
    const personalUser = readsBookmarks && user !== undefined ? user : null;

    const captureBookmarks = async (scope: BookmarkScope) => {
        const catalog = await sources.bookmarkCatalog(scope, signal);
        const gone = new Set<string>();
        await pMap(
            catalog,
            async (entry) => {
                const state = await sources.bookmarkState(scope, entry.id, signal);
                if (state === undefined) {
                    gone.add(entry.id);
                    return;
                }
                await write(hostDataNames.bookmarkState(scope, entry.id), state);
            },
            { concurrency: MAX_CONCURRENCY, signal },
        );
        await write(
            hostDataNames.bookmarkCatalog(scope),
            catalog.filter((entry) => !gone.has(entry.id)),
        );
    };

    // Groups whose ids the scene config doesn't carry: the viewer asks for them by group id.
    const groupIds = readsGroups
        ? (sceneConfig.objectGroups ?? []).flatMap((group) => (group.id && !group.ids ? [group.id] : []))
        : [];

    await Promise.all([
        step(async () => {
            const data = await sources.project(projectId, signal);
            await write(hostDataNames.project, { args: { projectId }, data } satisfies ProjectSnapshot);
            // The project's coordinate system, which the viewer looks up by its EPSG code as it opens.
            const epsg = data?.epsg;
            if (epsg) {
                const [wkt, proj4] = await Promise.all([
                    sources.epsgDefinition(epsg, "wkt", signal),
                    sources.epsgDefinition(epsg, "proj4", signal),
                ]);
                await write(hostDataNames.coordinateSystem, {
                    args: { epsg },
                    data: { wkt, proj4 },
                } satisfies CoordinateSystemSnapshot);
            }
        }),
        step(async () => {
            const data = await sources.modelTreePermissions(modelTreeProjectId, signal);
            await write(hostDataNames.modelTreePermissions, {
                args: { projectId: modelTreeProjectId },
                data,
            } satisfies ModelTreePermissionsSnapshot);
        }),
        ...(readsBookmarks ? [step(() => captureBookmarks("public"))] : []),
        ...(personalUser !== null ? [step(() => captureBookmarks("personal"))] : []),
        step(() =>
            pMap(
                groupIds,
                async (groupId) => {
                    await write(hostDataNames.groupIds(groupId), await sources.groupIds(groupId, signal));
                },
                { concurrency: MAX_CONCURRENCY, signal },
            ),
        ),
        ...(readsDeviations
            ? [
                  step(async () => {
                      const profiles = await sources.deviationProfiles(signal);
                      if (profiles !== undefined) {
                          await write(hostDataNames.deviationProfiles, profiles);
                      }
                  }),
                  step(async () => {
                      const config = (await sources.committedDeviations(signal)) ?? null;
                      await write(hostDataNames.committedDeviations, { config } satisfies CommittedDeviations);
                  }),
              ]
            : []),
    ]);

    // A version published while the capture ran: some of what it has may be the newer one's.
    await checkVersion();

    await write(hostDataNames.meta, {
        schemaVersion: HOST_DATA_SCHEMA_VERSION,
        projectId,
        manifestId: version.manifestId,
        capturedAt: new Date().toISOString(),
        bookmarks: { captured: readsBookmarks, personalUser },
    } satisfies HostDataMeta);
}

/**
 * The server serves another version of the scene than the sync downloaded: one was published
 * while the sync ran. Its host data is the newer version's, which doesn't fit the files.
 */
export class SceneChangedError extends Error {
    constructor(
        readonly downloaded: string,
        readonly served: string,
    ) {
        super(
            `Capturing host data: the scene changed during the download (downloaded ${downloaded}, the server now serves ${served}); sync again`,
        );
        this.name = "SceneChangedError";
    }
}

/** A capture request that got an answer, but not one to capture. */
class HostDataRequestError extends Error {
    constructor(
        readonly what: string,
        readonly status: number,
    ) {
        super(`Capturing host data: ${what} responded ${status}`);
        this.name = "HostDataRequestError";
    }
}

/**
 * {@link HostDataSources} over the network, for the scene `projectId`.
 * @param dataV2 Issues a data-v2 request with the viewer's headers (base url and auth).
 * @param committedDeviationsUrl The scene's `deviations.json` asset url.
 */
export function createHostDataSources({
    projectId,
    dataV2,
    sceneIndexUrl,
    committedDeviationsUrl,
}: {
    projectId: string;
    dataV2: (path: string, init: RequestInit) => Promise<Response>;
    /** The scene index the sync downloads from. */
    sceneIndexUrl: URL;
    committedDeviationsUrl: () => Promise<string>;
}): HostDataSources {
    /**
     * The response as the viewer's RTK Query reads it: JSON, or `null` for an empty body (the
     * data-v2 server answers 200 with none for, say, a scene without saved deviation profiles).
     * `undefined` for a status in `none`; otherwise, not OK, a failure.
     */
    const json = async <T>(
        what: string,
        path: string,
        init: RequestInit,
        none: readonly number[] = [],
    ): Promise<T | null | undefined> => {
        const res = await dataV2(path, init);
        if (none.includes(res.status)) {
            return undefined;
        }
        if (!res.ok) {
            throw new HostDataRequestError(what, res.status);
        }
        const text = await res.text();
        if (!text) {
            return null;
        }
        try {
            return JSON.parse(text) as T;
        } catch (error) {
            throw new Error(
                `Capturing host data: ${what} isn't JSON (${error instanceof Error ? error.message : String(error)})`,
                { cause: error },
            );
        }
    };
    /** As {@link json}, for an answer the capture works from (a list it goes through): an empty one fails. */
    const present = <T>(what: string, value: T | null | undefined): T | undefined => {
        if (value === null) {
            throw new Error(`Capturing host data: ${what} answered with an empty body`);
        }
        return value;
    };
    const required = async <T>(what: string, path: string, init: RequestInit): Promise<T> =>
        present(what, await json<T>(what, path, init)) as T;

    return {
        sceneConfig: (signal) => required("the scene config", `/api/scenes/${projectId}`, { signal }),
        sceneManifestId: async (signal) => {
            // Revalidated, not taken from the browser's cache: a cached index would name the
            // version the sync scanned after the server has moved on, and pass the check.
            const res = await fetch(sceneIndexUrl, { signal, cache: "no-cache" });
            if (!res.ok) {
                throw new HostDataRequestError("the scene index", res.status);
            }
            const manifest = ((await res.json()) as SceneIndex).offline?.manifest;
            if (typeof manifest !== "string") {
                throw new Error("Capturing host data: the scene index names no offline manifest");
            }
            return manifest;
        },
        // These, a state and a group's ids are captured as the viewer reads them, an empty
        // answer as `null`: online it takes that in its stride (`null` ids are an empty group).
        project: async (id, signal) => (await json<ProjectInfo>("the project", `/projects/${id}`, { signal })) ?? null,
        epsgDefinition: async (epsg, format, signal) => {
            // As the viewer asks it: an answer that isn't OK is no definition, not a failure.
            const res = await fetch(epsgDefinitionUrl(epsg, format), { signal });
            return res.ok ? await res.text() : null;
        },
        modelTreePermissions: async (id, signal) =>
            (await json<ModelTreePermission[]>("the model tree permissions", `/explorer/${id}/modeltree/permissions`, {
                signal,
            })) ?? null,
        permissions: async (scope, signal) => {
            const permissions = Object.values(Permission);
            const granted = await required<boolean[]>("the permission check", "/roles/check-permissions", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ scope, permissionIds: permissions }),
                signal,
            });
            return grantedPermissions(permissions, granted);
        },
        bookmarkCatalog: (scope, signal) =>
            required(`the ${scope} bookmark catalog`, `/explorer/v3/${projectId}/bookmarks/${scope}/catalog`, {
                signal,
            }),
        bookmarkState: (scope, bookmarkId, signal) =>
            json<Bookmark>(
                `${scope} bookmark ${bookmarkId}`,
                bookmarkStatePath({ projectId, bookmarkId, personal: scope === "personal" }),
                { signal },
                [404],
            ),
        groupIds: async (groupId, signal) =>
            (await json<number[]>(`group ${groupId}'s ids`, `/explorer/${projectId}/groups/${groupId}/ids`, {
                signal,
            })) ?? null,
        // None saved is an empty body, captured as `null`, which the widget reads as none. A 404
        // captures nothing, and offline the widget then fails to load them, as it does online.
        deviationProfiles: (signal) =>
            json("the deviation profiles", `/explorer/${projectId}/deviations`, { signal }, [404]),
        committedDeviations: async (signal) => {
            // A plain asset, as the viewer fetches it: any response that isn't OK means none.
            const res = await fetch(await committedDeviationsUrl(), { signal });
            return res.ok ? ((await res.json()) as DeviationProjectConfig) : undefined;
        },
    };
}
